import { useEffect, useMemo } from 'react';

import { CampaignStoreItemTypes } from '#api/index.js';
import { EditPeerPageModalFlow } from '#components/organisms/modals/EditPeerPage/EditPeerPageModalFlow';
import { useAuthentication } from '@givebacks/api';
import {
  PeerParticipantsWith__Type,
  useShowCampaignPeer,
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import { Constants } from 'core';
import { useUserAccessPermissions } from 'core/Api';
import {
  ContactOrganizerModal,
  EndedCampaignAlert,
  ShareModal,
  ShareQRCodeModal,
} from 'core/Components';
import { useCauseParam, usePlatformShare } from 'core/Hooks';
import { BASE_URL } from 'core/Utils';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { RichTextDisplay } from 'rich-text';
import {
  Box,
  ButtonMantine,
  Card,
  Container,
  DEFAULT_PEER_PROFILE_BANNER_IMAGE,
  Divider,
  FullWidthButtonGroup,
  Group,
  KebabMenu,
  MobileBottomButton,
  Skeleton,
  Stack,
  Text,
  Title,
} from 'ui';
import { useDisclosure } from 'ui/Hooks';
import { IconShare } from 'ui/Icons';

import { useCrowdfundingProvider } from '../../../../Context';
import { HeaderImage } from '../../../components';
import { PeerDonationCard } from '../../../components/Cards/PeerDonationCard';
import classes from '../../../components/CausePageLayout.module.css';
import { usePeerCreditModal } from '../../../components/Modals/usePeerCreditModal';
import { PeerRecentDonations } from '../RecentDonations/RecentDonations';
import { ActivityLog } from './ActivityLog/ActivityLog';

const ParticipantHeaderButtons = ({
  causeId,
  campaignId,
  participantId,
  participantName,
  participantConvention,
  hasEditAccess,
}: {
  causeId: string;
  campaignId: string;
  participantId: string;
  participantName: string;
  participantConvention: string;
  hasEditAccess: boolean;
}) => {
  const { data: cause } = useCauseParam();

  const navigate = useNavigate();

  const [isContactModalOpen, contactHandlers] = useDisclosure(false);
  const [isShareModalOpen, shareHandlers] = useDisclosure(false);
  const [isQRCodeModalOpen, qrCodeHandlers] = useDisclosure(false);

  const { platformShare } = usePlatformShare();

  const participantPageLink = `${BASE_URL}${Constants.appRoutes.participantCenter(
    causeId,
    campaignId
  )}/participants/${participantId}`;

  const causePageRoute = `${Constants.appRoutes.cause(causeId)}`;
  const campaignPageRoute = `${Constants.appRoutes.campaign(causeId, campaignId)}`;
  const participantCenterRoute = `${Constants.appRoutes.participantCenter(causeId, campaignId)}`;

  return (
    <>
      <Group gap="xs">
        <ButtonMantine
          size="sm"
          variant="white"
          color="gray"
          leftSection={<IconShare />}
          onClick={() =>
            void platformShare(
              {
                share_options: { url: participantPageLink },
              },
              () => shareHandlers.open()
            )
          }
        >
          Share
        </ButtonMantine>
        <KebabMenu
          items={[
            {
              label: 'Contact Organizer',
              onClick: () => contactHandlers.open(),
            },
            {
              label: 'Share QR Code',
              onClick: () => qrCodeHandlers.open(),
              visible: hasEditAccess,
            },
            {
              withDivider: true,
              label: 'Visit Cause Page',
              onClick: () => navigate(causePageRoute),
            },
            { label: 'Visit Campaign Page', onClick: () => navigate(campaignPageRoute) },
            {
              label: `Visit ${participantConvention} Center`,
              onClick: () => navigate(participantCenterRoute),
            },
          ]}
          buttonProps={{ variant: 'white', color: 'gray', size: 'sm' }}
        />
      </Group>
      <ContactOrganizerModal
        causeId={causeId}
        campaignId={campaignId}
        opened={isContactModalOpen}
        onClose={contactHandlers.close}
      />
      <ShareModal
        opened={isShareModalOpen}
        onClose={shareHandlers.close}
        imgSrc={cause?.logo?.url}
        link={participantPageLink}
        title={`Share This ${participantConvention} Fundraising Page`}
        causeName={cause?.name}
        description="Share this link to invite friends"
      />
      <ShareQRCodeModal
        link={participantPageLink}
        description={`${participantName}'s Fundraising Page`}
        opened={isQRCodeModalOpen}
        onClose={qrCodeHandlers.close}
      />
    </>
  );
};

const ParticipantPageActions = ({
  participant,
  campaign,
}: {
  participant: PeerParticipantsWith__Type;
  campaign: CampaignStoreItemTypes;
}) => {
  const navigate = useNavigate();
  const { handleSelectPeer } = usePeerCreditModal();

  const hasEditAccess = participant?.can_write;

  return (
    <FullWidthButtonGroup wrap="wrap-reverse">
      <ButtonMantine
        variant="gradient"
        onClick={() => handleSelectPeer(participant.uuid, 'participants')}
      >
        Donate
      </ButtonMantine>
      {hasEditAccess ? (
        <EditPeerPageModalFlow peer={participant} campaign={campaign} />
      ) : campaign?.participant_options?.registration_open ? (
        <ButtonMantine onClick={() => navigate('register')}>
          {campaign?.participant_options.registration_call_to_action}
        </ButtonMantine>
      ) : null}
    </FullWidthButtonGroup>
  );
};

const ParticipantPage = () => {
  const { participant_uuid } = useParams();
  const { causeId, campaignId } = useCrowdfundingProvider();

  const { isAuthenticated } = useAuthentication();
  const { data: permissions } = useUserAccessPermissions({
    causeId,
    accessType: 'fundraiser',
  });
  const {
    data: campaign,
    isLoading: isCampaignLoading,
    isError: isCampaignError,
  } = useShowConsumerCampaignById({
    causeId,
    campaignId,
  });

  const {
    data: participant,
    isLoading: isParticipantLoading,
    isError: isParticipantError,
    refetch,
  } = useShowCampaignPeer({
    peerType: 'participants',
    peerId: participant_uuid,
    causeId,
    campaignId,
    queryOptions: {
      options: {
        credits: true,
      },
    },
  });

  const { data: settings } = useShowConsumerCampaignSettingsById({ causeId, campaignId });

  const hasEditAccess = useMemo(() => {
    if (isAuthenticated) {
      if (permissions && permissions?.write) {
        return true;
      }
      if (participant && participant?.can_write) {
        return true;
      }
    }
    return false;
  }, [participant, permissions, isAuthenticated]);

  const hasActivityLogAccess = useMemo(() => {
    if (campaign) {
      if (
        campaign?.__type === 'athon' &&
        campaign?.participant_options?.activity_enabled &&
        // activity log is only visible to users with edit access: the participant to whom the page belongs or an admin
        hasEditAccess
      ) {
        return true;
      }
    }
    return false;
  }, [campaign, hasEditAccess]);

  useEffect(() => {
    refetch();
  }, [isAuthenticated]);

  if (isParticipantError) {
    // if the participant is not found, navigate to the participant center to view all and search participants
    return <Navigate to={'../../participant-center/participants'} />;
  }

  if (isCampaignError) {
    // if the campaign is not found, navigate to the cause page to view all campaigns
    return <Navigate to={'../../'} />;
  }

  if (!participant || !campaign) return null;

  return (
    <Container fluid className={classes.container}>
      <Stack className={classes.leftContent}>
        <HeaderImage
          isLoading={isCampaignLoading}
          src={participant.image?.url ?? DEFAULT_PEER_PROFILE_BANNER_IMAGE}
          headerButtons={
            <ParticipantHeaderButtons
              causeId={campaign.cause_id}
              campaignId={campaign.uuid}
              participantId={participant.uuid}
              participantName={`${participant.first_name} ${participant.last_name}`}
              participantConvention={campaign?.participant_options.convention_singular}
              hasEditAccess={hasEditAccess}
            />
          }
          backTo="../../participant-center"
        />
        <Stack>
          <Skeleton visible={isParticipantLoading}>
            <Title
              order={1}
            >{`${participant?.first_name} ${participant?.last_name}'s Fundraising Page`}</Title>
          </Skeleton>
          {participant.description && (
            <Skeleton visible={isParticipantLoading}>
              <Text type="body" style={{ whiteSpace: 'pre-wrap' }}>
                {participant.description}
              </Text>
            </Skeleton>
          )}
        </Stack>
        {settings?.isCampaignReadOnly && <EndedCampaignAlert type="campaign" hiddenFrom="md" />}
        <Stack>
          {hasEditAccess && <Title order={2}>My Activity</Title>}
          <PeerDonationCard type={participant.__type} peer={participant} campaign={campaign} />
          {hasActivityLogAccess && <ActivityLog participant={participant} />}
        </Stack>

        {campaign?.description && (
          <Skeleton visible={isCampaignLoading}>
            <Stack>
              <Title order={2}>About {campaign?.name}</Title>
              <Text type="body" style={{ whiteSpace: 'pre-wrap' }}>
                <RichTextDisplay html={campaign?.description} />
              </Text>
            </Stack>
          </Skeleton>
        )}
        {(hasEditAccess || campaign?.show_recent_donations) && (
          <PeerRecentDonations
            uuid={participant.uuid}
            causeId={campaign.cause_id}
            campaignId={campaign.uuid}
            peerType={'participants'}
          />
        )}
      </Stack>
      <Box className={classes.rightContent}>
        <Box className={classes.rightContentChild}>
          <Card p="xl" shadow="md">
            <Stack gap="lg">
              <Text c="gray" ta="center">
                Help {participant?.first_name} reach their goal!
              </Text>
              <Divider c="cobalt.0" />
              {settings?.isCampaignReadOnly ? (
                <EndedCampaignAlert type="campaign" />
              ) : (
                <Group gap="sm" grow>
                  <ParticipantPageActions participant={participant} campaign={campaign} />
                </Group>
              )}
            </Stack>
          </Card>
        </Box>
      </Box>
      {!settings?.isCampaignReadOnly && (
        <MobileBottomButton>
          <ParticipantPageActions participant={participant} campaign={campaign} />
        </MobileBottomButton>
      )}
    </Container>
  );
};

export { ParticipantPage };
